import { createApi } from '@reduxjs/toolkit/query/react';
import apiEndpoints from 'api/endpoints';

import { baseQueryWrapper } from './baseApi';

// Billing gets its own reducer path (rather than injecting into the shared
// baseAPI) so its cache can be excluded from redux-persist on its own —
// balance/payment data changes frequently (e.g. after a server-side auto
// top-up) and shouldn't be served stale from localStorage on reload, but
// the other domains sharing baseAPI still benefit from that persistence.
export const billingApi = createApi({
    reducerPath: 'billing-api',
    baseQuery: baseQueryWrapper,
    endpoints: (builder) => ({
        getCurrentCredit: builder.query({
            query: ({ companyUUID }) => ({
                url: `${apiEndpoints.currentCredit}?companyUUID=${companyUUID}`,
                method: 'GET',
            }),
        }),

        getInvoices: builder.query({
            query: ({ companyUUID, page, pageSize }) => ({
                url: `${apiEndpoints.invoiceData}?companyUUID=${companyUUID}&page=${page}&pageSize=${pageSize}`,
                method: 'GET',
            }),
        }),

        createPaymentSession: builder.mutation({
            query: ({ companyUUID, amount, allowRedisplay }) => ({
                url: apiEndpoints.billingData,
                method: 'POST',
                body: { companyAccessCode: companyUUID, amount, allowRedisplay },
            }),
        }),

        // No GET endpoint here — admin/company (already fetched app-wide via
        // refreshCompanyAction) already returns auto_topup_settings on the
        // company record, so a dedicated fetch would just duplicate that.
        updateAutoTopUpSettings: builder.mutation({
            query: ({ companyUUID, enabled, threshold, amount }) => ({
                url: apiEndpoints.autoTopUpSettings,
                method: 'PUT',
                body: { companyAccessCode: companyUUID, enabled, threshold, amount },
            }),
        }),

        getPaymentMethods: builder.query({
            query: ({ companyUUID }) => ({
                url: apiEndpoints.getPaymentMethods,
                method: 'POST',
                body: { companyAccessCode: companyUUID },
            }),
            providesTags: ['PaymentMethods'],
        }),

        savePaymentMethod: builder.mutation({
            query: ({ companyUUID, paymentMethodId, autoTopUpEnabled, allowRedisplay }) => ({
                url: apiEndpoints.createMethod,
                method: 'POST',
                body: {
                    companyAccessCode: companyUUID,
                    paymentMethod: paymentMethodId,
                    autoTopUpEnabled,
                    allowRedisplay,
                },
            }),
            invalidatesTags: ['PaymentMethods'],
        }),

        removePaymentMethod: builder.mutation({
            query: ({ companyUUID, paymentMethodId }) => ({
                url: apiEndpoints.removeCard,
                method: 'POST',
                body: { companyAccessCode: companyUUID, paymentMethod: paymentMethodId },
            }),
            invalidatesTags: ['PaymentMethods'],
        }),
    }),
});

export const {
    useGetCurrentCreditQuery,
    useGetInvoicesQuery,
    useCreatePaymentSessionMutation,
    useUpdateAutoTopUpSettingsMutation,
    useGetPaymentMethodsQuery,
    useSavePaymentMethodMutation,
    useRemovePaymentMethodMutation,
} = billingApi;

export default billingApi.middleware;
